import { useEffect, useState } from "react";
import { Course, Group, Staff, Store, StoreForReservation } from "../api/ApiData";

export type UTMParams = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
};

export type ReservationData = {
  rid: string | undefined;
  group: Group;
  utmParams: UTMParams;
  store: StoreForReservation | undefined;
  staff: Staff | undefined;
  course: Course | undefined;
  date: number | undefined;
  name: string;
  phone: string;
  mail: string;
  request: string;
  confirmed: boolean;
  posted: boolean;
  accepted: boolean;
  error: boolean;
  lineLoginUrl: string | null | undefined;
  fbc: string | undefined;
  fbp: string | undefined;
};

export type ReservationState = {
  data: ReservationData;
  setValue: <K extends keyof ReservationData>(key: K, value: ReservationData[K]) => void;
  form: 1 | 2 | 3 | "done" | "error";
  reset: () => void;
};

const useReservationState = (group: Group, utmParams: UTMParams, fbc?: string, fbp?: string): ReservationState => {
  const defaultReservationData = {
    rid: undefined,
    group,
    utmParams,
    store: undefined,
    staff: undefined,
    course: undefined,
    date: undefined,
    name: "",
    phone: "",
    mail: "",
    request: "",
    confirmed: false,
    posted: false,
    accepted: false,
    error: false,
    lineLoginUrl: undefined,
    fbc,
    fbp,
  };
  const [data, setData] = useState<ReservationData>(defaultReservationData);

  useEffect(() => {
    setData((prevData) => ({ ...prevData, date: undefined }));
  }, [data.course?.id]);

  useEffect(() => {
    setData((prevData) => ({ ...prevData, date: undefined }));
  }, [data.staff?.id]);

  useEffect(() => {
    setData((prevData) => ({ ...prevData, course: undefined, staff: undefined }));
  }, [data.store?.id]);

  const setValue = <K extends keyof ReservationData>(key: K, value: ReservationData[K]) => {
    // 連続してsetValueを呼び出すと、古い呼び出し側が反映されないため、関数をsetDataに渡す
    setData((prevData) => ({ ...prevData, [key]: value }));
  };

  let form: 1 | 2 | 3 | "done" | "error" = 1;
  if (data.date) form = 2;
  if (data.confirmed) form = 3;
  if (data.accepted) form = "done";
  if (data.error) form = "error";

  const reset = () => setData({ ...defaultReservationData });

  return { data, setValue, form, reset };
};

export default useReservationState;
